import { useMemo, useState } from "react";
import { motion } from "framer-motion";
import { Search, Heart, Flame, Sparkles, Target } from "lucide-react";
import { Reveal, SectionTitle, Overline } from "@/components/Reveal";
import { SignalModal } from "@/components/SignalModal";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { imgUrl } from "@/lib/api";
import { useLiveResource } from "@/hooks/useLiveResource";

const FAV_KEY = "mg_favorites";
const loadFavs = () => {
  try {
    return JSON.parse(localStorage.getItem(FAV_KEY) || "[]");
  } catch {
    return [];
  }
};

export const PopularGames = () => {
  const { data: gamesData } = useLiveResource("/public/games", { initial: [] });
  const games = gamesData || [];
  const [query, setQuery] = useState("");
  const [provider, setProvider] = useState("all");
  const [favs, setFavs] = useState(loadFavs);
  const [modalGame, setModalGame] = useState(null);

  const providers = useMemo(
    () => Array.from(new Set(games.map((g) => g.provider))),
    [games],
  );

  const toggleFav = (id) => {
    setFavs((prev) => {
      const next = prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id];
      localStorage.setItem(FAV_KEY, JSON.stringify(next));
      return next;
    });
  };

  const filtered = useMemo(() => {
    return games.filter((g) => {
      const okName = g.name.toLowerCase().includes(query.toLowerCase());
      const okProv = provider === "all" || g.provider === provider;
      return okName && okProv;
    });
  }, [games, query, provider]);

  return (
    <section id="jogos" className="relative py-20 md:py-28" data-testid="games-section">
      <div className="max-w-7xl mx-auto px-5 md:px-8">
        <Reveal className="mb-8">
          <Overline>Catálogo de Jogos</Overline>
          <SectionTitle className="mt-3">
            Jogos <span className="text-gold-gradient">Populares</span>
          </SectionTitle>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="glass-card p-4 md:p-5 flex flex-col md:flex-row gap-3 mb-10">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-white/40" />
              <input
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Pesquisar jogo..."
                className="w-full bg-white/[0.03] border border-white/10 rounded-full pl-11 pr-4 py-3 text-sm text-white placeholder:text-white/35 focus:outline-none focus:border-[#F5C542]/50 transition-colors"
                data-testid="games-search-input"
              />
            </div>
            <Select value={provider} onValueChange={setProvider}>
              <SelectTrigger
                className="md:w-56 h-12 rounded-full bg-white/[0.03] border-white/10 text-white px-5"
                data-testid="games-provider-filter"
              >
                <SelectValue placeholder="Provedora" />
              </SelectTrigger>
              <SelectContent className="bg-[#121212] border-white/10 text-white">
                <SelectItem value="all">Todas as Provedoras</SelectItem>
                {providers.map((p) => (
                  <SelectItem key={p} value={p}>
                    {p}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </Reveal>

        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 2xl:grid-cols-6 gap-4 md:gap-5">
          {filtered.map((g, i) => {
            const isFav = favs.includes(g.id);
            return (
              <motion.div
                key={g.id}
                layout
                initial={{ opacity: 0, y: 18 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.45, delay: (i % 6) * 0.05 }}
                className="group relative rounded-2xl overflow-hidden glass-gold p-0"
                data-testid={`game-card-${g.id}`}
              >
                {/* Image */}
                <div className="relative aspect-[3/4] overflow-hidden">
                  <img
                    src={imgUrl(g.image)}
                    alt={g.name}
                    loading="lazy"
                    className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-110"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-[#120D0D] via-[#120D0D]/20 to-transparent" />
                  <div className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-500 shadow-[inset_0_0_60px_rgba(245, 197, 66,0.25)]" />

                  {/* Badges */}
                  <div className="absolute top-2.5 left-2.5 flex flex-col gap-1.5">
                    {g.hot && (
                      <span className="flex items-center gap-1 text-[9px] font-bold uppercase text-[#FF3B30] bg-[#120D0D]/80 border border-[#FF3B30]/40 px-2 py-0.5 rounded-full backdrop-blur">
                        <Flame className="w-2.5 h-2.5" /> Hot
                      </span>
                    )}
                    {g.is_new && (
                      <span className="flex items-center gap-1 text-[9px] font-bold uppercase text-[#00FF41] bg-[#120D0D]/80 border border-[#00FF41]/40 px-2 py-0.5 rounded-full backdrop-blur">
                        <Sparkles className="w-2.5 h-2.5" /> Novo
                      </span>
                    )}
                  </div>

                  {/* Favorite */}
                  <button
                    onClick={() => toggleFav(g.id)}
                    aria-pressed={isFav}
                    data-active={isFav}
                    className="absolute top-2.5 right-2.5 w-8 h-8 rounded-full flex items-center justify-center bg-[#120D0D]/70 border border-white/10 backdrop-blur hover:border-[#F5C542]/50 transition-colors"
                    aria-label="Favoritar"
                    data-testid={`game-fav-${g.id}`}
                  >
                    <Heart
                      className={`w-4 h-4 transition-colors ${isFav ? "fill-[#F5C542] text-[#F5C542]" : "text-white/70"}`}
                    />
                  </button>

                  {/* Online */}
                  <span className="absolute bottom-2.5 left-2.5 flex items-center gap-1.5 text-[9px] font-bold uppercase text-[#00FF41] bg-[#120D0D]/70 border border-[#00FF41]/30 px-2 py-0.5 rounded-full backdrop-blur">
                    <span className="w-1.5 h-1.5 rounded-full bg-[#00FF41] animate-pulse" /> Online
                  </span>
                </div>

                {/* Info */}
                <div className="p-3.5">
                  <div className="text-[10px] text-[#F5C542] uppercase tracking-wider font-display truncate">
                    {g.provider}
                  </div>
                  <div className="font-display font-bold text-white text-sm leading-tight truncate mt-0.5">
                    {g.name}
                  </div>
                  <button
                    onClick={() => setModalGame(g)}
                    className="mt-3 w-full flex items-center justify-center gap-1.5 py-2.5 rounded-full btn-gold font-display font-bold text-xs hover:scale-[1.03] transition-transform"
                    data-testid={`game-signal-btn-${g.id}`}
                  >
                    <Target className="w-3.5 h-3.5" /> Gerar Sinal
                  </button>
                </div>
              </motion.div>
            );
          })}
        </div>

        {filtered.length === 0 && (
          <div className="text-center py-16 text-white/40" data-testid="games-empty">
            Nenhum jogo encontrado.
          </div>
        )}
      </div>

      {modalGame && <SignalModal game={modalGame} onClose={() => setModalGame(null)} />}
    </section>
  );
};
